import { useState, useEffect, useRef } from 'react'
import {
  ResponsiveContainer, ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, ReferenceLine,
} from 'recharts'
import { CHART_COLORS, MONEY, AXIS, GRID } from '../lib/chartTheme'

// Gráficos do fin — o "melhor dos dois mundos": visual do painel Financeiro do crm-twms
// (AreaChart com gradiente + donut animado + count-up) sobre os dados que o fin já monta.
// Portado de FinanceiroPanel.tsx (Recharts).

const brl = (n: number) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
export { CHART_COLORS }

// count-up com easing (número anima do valor anterior até o novo)
export function useCountUp(target: number, durationMs = 900) {
  const [v, setV] = useState(target)
  const fromRef = useRef(target)
  useEffect(() => {
    const start = fromRef.current
    const t0 = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / durationMs)
      const eased = 1 - Math.pow(1 - p, 3)
      setV(start + (target - start) * eased)
      if (p < 1) raf = requestAnimationFrame(tick)
      else fromRef.current = target
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, durationMs])
  return v
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="glass-popover neu-tip rounded-xl px-3 py-2 text-sm shadow-lg">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p: { name: string; value: number; color: string }) => (
        <p key={p.name} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-semibold text-foreground tabular-nums">{brl(p.value)}</span>
        </p>
      ))}
    </div>
  )
}

interface FluxoRow { label: string; recebimentos: number; pagamentos: number; saldo: number; transf_in: number; transf_out: number }

// Fluxo de caixa: saldo como ÁREA com gradiente (o hero, estilo CRM) + recebimentos/
// pagamentos como linhas finas. Curva animada, eixos formatados, tooltip glass.
export function CashflowChart({ rows, showTransfer, height = 260 }: { rows: FluxoRow[]; showTransfer: boolean; height?: number | `${number}%` }) {
  const data = rows.map(r => ({ ...r, transf: r.transf_in - r.transf_out }))
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 10, right: 14, left: 4, bottom: 0 }}>
        <defs>
          <linearGradient id="finFluxoFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={MONEY.balance} stopOpacity={0.5} />
            <stop offset="100%" stopColor={MONEY.balance} stopOpacity={0.03} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="4 4" stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 12, fill: AXIS }} tickLine={false} axisLine={{ stroke: 'hsl(var(--border))' }} dy={6} minTickGap={20} />
        <YAxis tick={{ fontSize: 12, fill: AXIS }} width={56} tickLine={false} axisLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))} />
        <Tooltip content={<ChartTooltip />} cursor={{ stroke: MONEY.balance, strokeWidth: 1, strokeDasharray: '4 4' }} />
        <Area type="monotone" dataKey="saldo" name="Saldo" stroke={MONEY.balance} strokeWidth={3} fill="url(#finFluxoFill)"
          dot={false} activeDot={{ r: 6, fill: MONEY.balance, stroke: '#fff', strokeWidth: 2 }} isAnimationActive animationDuration={1400} animationEasing="ease-out" />
        <Line type="monotone" dataKey="recebimentos" name="Recebimentos" stroke={MONEY.in} strokeWidth={2} dot={false} isAnimationActive animationDuration={1400} />
        <Line type="monotone" dataKey="pagamentos" name="Pagamentos" stroke={MONEY.out} strokeWidth={2} dot={false} isAnimationActive animationDuration={1400} />
        {showTransfer && <Line type="monotone" dataKey="transf" name="Transferências (líq.)" stroke={MONEY.transfer} strokeWidth={2} strokeDasharray="4 3" dot={false} isAnimationActive animationDuration={1400} />}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

// Donut por categoria — animado, com total no centro e legenda com valores.
// selected/onSelect (opcionais): clique na fatia ou na legenda seleciona a
// categoria — fatias fora da seleção esmaecem e a legenda marca a ativa.
export function CategoryDonut({ data, total, title, selected, onSelect }: {
  data: { name: string; value: number }[]; total: number; title?: string
  selected?: string | null; onSelect?: (name: string) => void
}) {
  if (!data.length) return null
  return (
    <div className="rounded-xl border border-border p-4 flex flex-col neu-plot">
      {title && <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>}
      <div className="relative">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={88} paddingAngle={3} stroke="none"
              isAnimationActive animationDuration={1200} animationEasing="ease-out"
              onClick={onSelect ? (_: unknown, i: number) => { const n = data[i]?.name; if (n) onSelect(n) } : undefined}>
              {data.map((c, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}
                  fillOpacity={selected && selected !== c.name ? 0.3 : 1}
                  cursor={onSelect ? 'pointer' : undefined} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</span>
          {/* font-[700] e não font-bold: a régua PLANOS leva .text-lg.font-bold a 26px (cartão de número) e o total estourava o donut */}
          <span className="text-[15px] font-[700] text-foreground tabular-nums">{brl(total)}</span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-y-1.5">
        {data.map((c, i) => {
          const inner = (
            <>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
              <span className="text-muted-foreground truncate flex-1 text-left">{c.name}</span>
              <span className="font-semibold text-foreground tabular-nums">{brl(c.value)}</span>
            </>
          )
          return onSelect ? (
            <button key={c.name} onClick={() => onSelect(c.name)} title={`Destacar ${c.name} na tabela`}
              className={`flex items-center gap-2 text-sm min-w-0 rounded-md px-1 -mx-1 transition-colors ${selected === c.name ? 'bg-primary/10' : 'hover:bg-secondary'}`}>
              {inner}
            </button>
          ) : (
            <div key={c.name} className="flex items-center gap-2 text-sm min-w-0">{inner}</div>
          )
        })}
      </div>
    </div>
  )
}


// ── Mês a mês (21/10/2026) ──────────────────────────────────────────────────
// Desenho trazido do /seals a pedido do diretor: entradas sobem, saídas descem
// (stackOffset sign), a parte cheia é o REALIZADO e a clara é o que está A VENCER;
// a linha é o saldo no fim de cada mês. Clicar num mês o seleciona (o resto da
// tela — cartões e categorias — segue o mês escolhido). O mês escolhido fica
// opaco e os outros esmaecem um pouco.
export interface MesFluxo { mes: string; saldo_inicial: number; entradas: number; saidas: number; transf_in: number; transf_out: number; entradas_prev: number; saidas_prev: number; saldo_final: number; futuro: boolean }
const MESES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
export const mesCurto = (m: string) => { const [y, mo] = m.split('-').map(Number); return `${MESES_CURTO[mo - 1]}/${String(y).slice(2)}` }
const brlK = (v: number) => { const a = Math.abs(v); const s = a >= 1e6 ? `${(a / 1e6).toFixed(1).replace('.', ',')} mi` : a >= 1e3 ? `${Math.round(a / 1e3)} mil` : String(Math.round(a)); return (v < 0 ? '−' : '') + s }

// Barras de entradas × saídas para QUALQUER recorte (dia, mês do ano, período) —
// o mesmo desenho da Visão geral nas outras visões do Fluxo (21/10/2026: o dono
// cobrou que Diário/Ano/Período ficaram para trás). Sem `selected`, todas as
// barras ficam cheias; com ele, o escolhido fica opaco e os outros esmaecem.
export interface PontoFluxo { key: string; label: string; titulo?: string; entradas: number; saidas: number; entradasPrev?: number; saidasPrev?: number; saldo: number; futuro?: boolean }

export function FlowBarsChart({ pontos, selected, onSelect, barSize = 38 }: { pontos: PontoFluxo[]; selected?: string; onSelect?: (key: string) => void; barSize?: number }) {
  const data = pontos.map(p => ({ ...p, entradasPrev: p.entradasPrev || 0, saidas: -p.saidas, saidasPrev: -(p.saidasPrev || 0) }))
  const op = (key: string, base = 1) => (!selected || key === selected ? base : base * 0.55)
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} stackOffset="sign" margin={{ top: 10, right: 12, left: 4, bottom: 0 }}
        onClick={onSelect ? (e => { const p = (e as { activePayload?: { payload: { key: string } }[] })?.activePayload?.[0]?.payload; if (p) onSelect(p.key) }) : undefined}
        style={onSelect ? { cursor: 'pointer' } : undefined}>
        <CartesianGrid strokeDasharray="4 4" stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 12, fill: AXIS }} tickLine={false} axisLine={false} dy={6} minTickGap={8} />
        <YAxis tick={{ fontSize: 12, fill: AXIS }} width={64} tickLine={false} axisLine={false} tickFormatter={v => brlK(Number(v))} />
        <ReferenceLine y={0} stroke={GRID} />
        <Tooltip cursor={{ fill: 'rgba(0,82,78,0.06)' }} content={({ active, payload }) => {
          if (!active || !payload?.length) return null
          const p = payload[0].payload as typeof data[number]
          return (
            <div className="glass-popover neu-tip rounded-xl px-3 py-2 text-sm shadow-lg min-w-[220px]">
              <p className="font-semibold text-foreground mb-1">{p.titulo || p.label}{p.futuro ? ' · previsto' : ''}</p>
              <p className="flex justify-between gap-4"><span className="text-muted-foreground">Entradas</span><span className="tabular-nums">{brl(p.entradas)}</span></p>
              {p.entradasPrev > 0 && <p className="flex justify-between gap-4"><span className="text-muted-foreground">a receber</span><span className="tabular-nums">{brl(p.entradasPrev)}</span></p>}
              <p className="flex justify-between gap-4"><span className="text-muted-foreground">Saídas</span><span className="tabular-nums">{brl(-p.saidas)}</span></p>
              {p.saidasPrev < 0 && <p className="flex justify-between gap-4"><span className="text-muted-foreground">a pagar</span><span className="tabular-nums">{brl(-p.saidasPrev)}</span></p>}
              <p className="flex justify-between gap-4 font-semibold mt-1"><span>Saldo no fim</span><span className="tabular-nums">{brl(p.saldo)}</span></p>
            </div>
          )
        }} />
        <Bar dataKey="entradas" stackId="f" fill={MONEY.in} maxBarSize={barSize}>
          {data.map(d => <Cell key={d.key} fillOpacity={op(d.key)} />)}
        </Bar>
        <Bar dataKey="entradasPrev" stackId="f" fill={MONEY.in} radius={[6, 6, 0, 0]} maxBarSize={barSize}>
          {data.map(d => <Cell key={d.key} fillOpacity={op(d.key, 0.35)} />)}
        </Bar>
        <Bar dataKey="saidas" stackId="f" fill={MONEY.out} maxBarSize={barSize}>
          {data.map(d => <Cell key={d.key} fillOpacity={op(d.key)} />)}
        </Bar>
        <Bar dataKey="saidasPrev" stackId="f" fill={MONEY.out} radius={[0, 0, 6, 6]} maxBarSize={barSize}>
          {data.map(d => <Cell key={d.key} fillOpacity={op(d.key, 0.35)} />)}
        </Bar>
        <Line type="monotone" dataKey="saldo" stroke={MONEY.balance} strokeWidth={2.6}
          dot={pontos.length > 16 ? false : { r: 3.5, fill: MONEY.balance, strokeWidth: 0 }} activeDot={{ r: 5 }} isAnimationActive animationDuration={900} />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export function MonthlyFlowChart({ serie, selected, onSelect }: { serie: MesFluxo[]; selected: string; onSelect: (mes: string) => void }) {
  return <FlowBarsChart selected={selected} onSelect={onSelect} pontos={serie.map(m => ({
    key: m.mes, label: mesCurto(m.mes), entradas: m.entradas, saidas: m.saidas,
    entradasPrev: m.entradas_prev, saidasPrev: m.saidas_prev, saldo: m.saldo_final, futuro: m.futuro,
  }))} />
}
